import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { FeedOutlined, FilterListOutlined, RefreshOutlined, SaveOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/Api';
import {
  getApiErrorMessage,
  getClassDisplayName,
  getSchoolId,
  getStoredTeacher,
  teacherPortalGridItemSx,
  teacherPortalPanelCardSx,
  teacherPortalRowSx,
} from './teacherPortalUtils';

const weekNumber = (week) => Number(String(week || '').replace('Week ', '')) || 1;

export default function TeacherExercisesScreen() {
  const theme = useTheme();
  const teacher = getStoredTeacher();
  const schoolId = getSchoolId(teacher);
  const classes = Array.isArray(teacher.assignedClasses) ? teacher.assignedClasses : [];
  const [terms, setTerms] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(classes[0]?._id || '');
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [count, setCount] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedTerm = useMemo(() => terms.find((term) => term._id === selectedTermId), [selectedTermId, terms]);

  const loadTerms = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await api.get('/term/all', { params: { school: schoolId } });
      const nextTerms = res.data?.allTerms || res.data?.terms || [];
      setTerms(nextTerms);
      setSelectedTermId((prev) => prev || nextTerms[0]?._id || '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load terms.'));
    }
  }, [schoolId]);

  const loadWeeks = useCallback(async () => {
    if (!schoolId || !selectedTerm) return;
    try {
      const res = await api.get('/term/weeks', {
        params: {
          school: schoolId,
          academicYear: selectedTerm.academicYear,
          term: selectedTerm.term,
          termId: selectedTerm._id,
        },
      });
      const fetchedWeeks = res.data?.weeks || [];
      const labels = fetchedWeeks
        .map((week) => week.label)
        .filter(Boolean)
        .sort((a, b) => weekNumber(a) - weekNumber(b));
      const currentWeekLabel = fetchedWeeks.find((week) => week?.isCurrent && week?.label)?.label;

      setWeeks(labels);
      setSelectedWeek(currentWeekLabel || labels[0] || '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load weeks.'));
    }
  }, [schoolId, selectedTerm]);

  const loadRecords = useCallback(async () => {
    if (!selectedClassId || !selectedTermId || !selectedWeek) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/exercises/summary', {
        params: {
          classId: selectedClassId,
          termId: selectedTermId,
          week: weekNumber(selectedWeek),
          limit: 50,
        },
      });
      const nextRecords = res.data?.data || [];
      setRecords(nextRecords);
      const mine = nextRecords.find((record) => String(record.teacher?._id) === String(teacher.teacherId));
      setCount(mine ? String(mine.totalExercises || 0) : '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load exercises.'));
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, selectedTermId, selectedWeek, teacher.teacherId]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  useEffect(() => {
    setSelectedWeek('');
    loadWeeks();
  }, [loadWeeks]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSave = async (event) => {
    event.preventDefault();
    if (!selectedClassId || !selectedTermId || !selectedWeek) return;
    const parsed = Number(count);
    if (!Number.isInteger(parsed) || parsed < 0) {
      setError('Exercise count must be a whole number.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await api.post('/exercises', {
        classId: selectedClassId,
        termId: selectedTermId,
        week: weekNumber(selectedWeek),
        count: parsed,
      });
      toast.success('Exercise count saved.');
      loadRecords();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save exercises.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Weekly Exercises</Typography>
          <Typography color="text.secondary">Record exercise checks for the selected week.</Typography>
        </Box>
        <Button startIcon={<RefreshOutlined />} onClick={loadRecords} disabled={loading} variant="outlined" sx={{ textTransform: 'none' }}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent>
              <Stack component="form" onSubmit={handleSave} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <FilterListOutlined fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Exercise Setup
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select the class, term, and week.
                    </Typography>
                  </Box>
                </Stack>
                <FormControl fullWidth>
                  <InputLabel>Class</InputLabel>
                  <Select label="Class" value={selectedClassId} onChange={(event) => setSelectedClassId(event.target.value)}>
                    {classes.map((cls) => <MenuItem key={cls._id} value={cls._id}>{getClassDisplayName(cls)}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Term</InputLabel>
                  <Select label="Term" value={selectedTermId} onChange={(event) => setSelectedTermId(event.target.value)}>
                    {terms.map((term) => <MenuItem key={term._id} value={term._id}>{term.term} • {term.academicYear}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Week</InputLabel>
                  <Select label="Week" value={selectedWeek} onChange={(event) => setSelectedWeek(event.target.value)}>
                    {weeks.map((week) => <MenuItem key={week} value={week}>{week}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField label="Total exercises" type="number" value={count} onChange={(event) => setCount(event.target.value)} fullWidth />
                <Button type="submit" variant="contained" startIcon={<SaveOutlined />} disabled={saving} sx={{ textTransform: 'none', borderRadius: 1 }}>
                  Save Exercises
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <FeedOutlined fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Exercise Records ({records.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Summary for {selectedWeek || 'the selected week'}.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={1.5}>
                  {records.map((record) => (
                    <Box key={record._id} sx={teacherPortalRowSx(theme)}>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Box>
                          <Typography fontWeight={900}>{record.teacher?.name || 'Teacher'}</Typography>
                          <Typography color="text.secondary">Week {record.week}</Typography>
                        </Box>
                        <Typography variant="h5" fontWeight={900}>{record.totalExercises || 0}</Typography>
                      </Stack>
                    </Box>
                  ))}
                  {!loading && records.length === 0 && (
                  <Typography color="text.secondary">No exercise record found for this filter.</Typography>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
